/**
 * All Material Typography Tokens.
 *
 * This is the only place where you should list Material Typography Tokens in JavaScript.
 *
 * Utility methods generate the Typescript Interface from this list.
 *
 * Only the keys are ever read (the values are placeholders). They are `null` rather than
 * `undefined` because type tooling that parses this literal (the JSON Schema generator in
 * tools/json-schema) can represent a null literal but not the `undefined` identifier.
 */
export const MaterialTypographyTokenKeys = {
  '--mat-sys-display-large': null,
  '--mat-sys-display-large-font': null,
  '--mat-sys-display-large-line-height': null,
  '--mat-sys-display-large-size': null,
  '--mat-sys-display-large-tracking': null,
  '--mat-sys-display-large-weight': null,
  '--mat-sys-display-medium': null,
  '--mat-sys-display-medium-font': null,
  '--mat-sys-display-medium-line-height': null,
  '--mat-sys-display-medium-size': null,
  '--mat-sys-display-medium-tracking': null,
  '--mat-sys-display-medium-weight': null,
  '--mat-sys-display-small': null,
  '--mat-sys-display-small-font': null,
  '--mat-sys-display-small-line-height': null,
  '--mat-sys-display-small-size': null,
  '--mat-sys-display-small-tracking': null,
  '--mat-sys-display-small-weight': null,
  '--mat-sys-headline-large': null,
  '--mat-sys-headline-large-font': null,
  '--mat-sys-headline-large-line-height': null,
  '--mat-sys-headline-large-size': null,
  '--mat-sys-headline-large-tracking': null,
  '--mat-sys-headline-large-weight': null,
  '--mat-sys-headline-medium': null,
  '--mat-sys-headline-medium-font': null,
  '--mat-sys-headline-medium-line-height': null,
  '--mat-sys-headline-medium-size': null,
  '--mat-sys-headline-medium-tracking': null,
  '--mat-sys-headline-medium-weight': null,
  '--mat-sys-headline-small': null,
  '--mat-sys-headline-small-font': null,
  '--mat-sys-headline-small-line-height': null,
  '--mat-sys-headline-small-size': null,
  '--mat-sys-headline-small-tracking': null,
  '--mat-sys-headline-small-weight': null,
  '--mat-sys-title-large': null,
  '--mat-sys-title-large-font': null,
  '--mat-sys-title-large-line-height': null,
  '--mat-sys-title-large-size': null,
  '--mat-sys-title-large-tracking': null,
  '--mat-sys-title-large-weight': null,
  '--mat-sys-title-medium': null,
  '--mat-sys-title-medium-font': null,
  '--mat-sys-title-medium-line-height': null,
  '--mat-sys-title-medium-size': null,
  '--mat-sys-title-medium-tracking': null,
  '--mat-sys-title-medium-weight': null,
  '--mat-sys-title-small': null,
  '--mat-sys-title-small-font': null,
  '--mat-sys-title-small-line-height': null,
  '--mat-sys-title-small-size': null,
  '--mat-sys-title-small-tracking': null,
  '--mat-sys-title-small-weight': null,
  '--mat-sys-body-large': null,
  '--mat-sys-body-large-font': null,
  '--mat-sys-body-large-line-height': null,
  '--mat-sys-body-large-size': null,
  '--mat-sys-body-large-tracking': null,
  '--mat-sys-body-large-weight': null,
  '--mat-sys-body-medium': null,
  '--mat-sys-body-medium-font': null,
  '--mat-sys-body-medium-line-height': null,
  '--mat-sys-body-medium-size': null,
  '--mat-sys-body-medium-tracking': null,
  '--mat-sys-body-medium-weight': null,
  '--mat-sys-body-small': null,
  '--mat-sys-body-small-font': null,
  '--mat-sys-body-small-line-height': null,
  '--mat-sys-body-small-size': null,
  '--mat-sys-body-small-tracking': null,
  '--mat-sys-body-small-weight': null,
  '--mat-sys-label-large': null,
  '--mat-sys-label-large-font': null,
  '--mat-sys-label-large-line-height': null,
  '--mat-sys-label-large-size': null,
  '--mat-sys-label-large-tracking': null,
  '--mat-sys-label-large-weight': null,
  '--mat-sys-label-large-weight-prominent': null,
  '--mat-sys-label-medium': null,
  '--mat-sys-label-medium-font': null,
  '--mat-sys-label-medium-line-height': null,
  '--mat-sys-label-medium-size': null,
  '--mat-sys-label-medium-tracking': null,
  '--mat-sys-label-medium-weight': null,
  '--mat-sys-label-medium-weight-prominent': null,
  '--mat-sys-label-small': null,
  '--mat-sys-label-small-font': null,
  '--mat-sys-label-small-line-height': null,
  '--mat-sys-label-small-size': null,
  '--mat-sys-label-small-tracking': null,
  '--mat-sys-label-small-weight': null,
} as const;

/**
 * These are the shorthand keys for the Material Typography Tokens.
 *
 * They cannot be set through the JSON API.
 */
export const MaterialTypographyTokenKeysShorthand = {
  '--mat-sys-display-large': null,
  '--mat-sys-display-medium': null,
  '--mat-sys-display-small': null,
  '--mat-sys-headline-large': null,
  '--mat-sys-headline-medium': null,
  '--mat-sys-headline-small': null,
  '--mat-sys-title-large': null,
  '--mat-sys-title-medium': null,
  '--mat-sys-title-small': null,
  '--mat-sys-body-large': null,
  '--mat-sys-body-medium': null,
  '--mat-sys-body-small': null,
  '--mat-sys-label-large': null,
  '--mat-sys-label-medium': null,
  '--mat-sys-label-small': null,
} as const;

////////////////////////////////////////////////////////////////////////////////////////
// DO NOT MODIFY BELOW THIS LINE
////////////////////////////////////////////////////////////////////////////////////////

/**
 * Generates System Tokens from the provided Typography.
 */
export const GenerateMaterialTypographySystemTokens = (typography?: MaterialTypographyTokens) => {
  return typography;
};

/**
 * Helper TypeScript syntax for mapping keys from a constant to a type.
 */
type MappedInterface<T> = {
  [K in keyof T]?: string;
};

/**
 * Generated Interface for the Material Typography Tokens.
 */
export interface MaterialTypographyTokens extends Partial<MappedInterface<typeof MaterialTypographyTokenKeys>> {}
